import { FC, useEffect, useMemo } from 'react';
import { Stack } from '@mui/material';
import { ROUTES } from 'config';
import {
  AutocompleteInput,
  Button,
  DataLossPrompt,
  FileInput,
  Footer,
  ImageList,
  Loader,
  MultiLanguageInputContainer,
  NothingFoundAlert,
  NumberInput,
  PageContentLayout,
  PageHeaderLayout,
  Panel,
  SelectInput,
  SideMenu,
  SwitchInput,
  TextField,
} from 'melp-design/components';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ContentLanguage, FrequencyUnit, frequencyUnits } from 'types/general';
import { Translations } from 'types/Common';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';
import { Delete } from '@mui/icons-material';
import { formatLanguage } from 'utils/format';
import { useUploadFile } from 'store/files';
import { useCompanyLanguages } from 'state/Administrators';
import { useEmployeeGroups } from 'store/employee-groups';
import {
  useCreateRecognitionAchievement,
  useRecognitionAchievement,
  useUpdateRecognitionAchievement,
} from 'store/recognition-achievements';
import { usePointsLabel } from 'containers/Recognition/common/usePointsLabel';

interface Values {
  languages: Array<Uppercase<ContentLanguage>>;
  name: string;
  points: number;
  requiresApproval: boolean;
  limit: number;
  limitPeriod: FrequencyUnit;
  imageId: string;
  translations: Translations<{ title: string; description: string }>;
  employeeGroups: string[];
}

const getInitialValues = (
  defaultLanguage: Uppercase<ContentLanguage>,
): Values => ({
  languages: [defaultLanguage],
  name: '',
  points: NaN,
  requiresApproval: false,
  limit: 0,
  limitPeriod: 'monthly',
  imageId: '',
  translations: [{ language: defaultLanguage, title: '', description: '' }],
  employeeGroups: [],
});

export const AchievementDetails: FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { id: achievementId } = useRouteParams(
    ROUTES.recognitions.achievements.details,
  );
  const predefinedToasts = usePredefinedToasts();
  const { defaultLanguage, supportedLanguages } = useCompanyLanguages();
  const pointsLabel = usePointsLabel();

  const INITIAL_VALUES = useMemo(
    () => getInitialValues(defaultLanguage),
    [defaultLanguage],
  );

  const { data: achievement, isLoading } =
    useRecognitionAchievement(achievementId);
  const { data: employeesGroups, isLoading: isEmployeeGroupsLoading } =
    useEmployeeGroups();

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    reset,
    formState: { dirtyFields },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });
  const { fields: translationsFields, replace: replaceTranslations } =
    useFieldArray({ name: 'translations', control });

  useEffect(() => {
    if (achievement) {
      reset({
        languages: achievement.translations.map((trans) => trans.language),
        name: achievement.name,
        points: achievement.points,
        requiresApproval: achievement.requiresApproval,
        limit: achievement.limit,
        limitPeriod: achievement.limitPeriod,
        imageId: achievement.image?.id ?? '',
        employeeGroups: achievement.employeeGroups.map((group) => group.id),
        translations: achievement.translations,
      });
    }
  }, [achievement, reset]);

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();
  const { mutate: createAchievement, isLoading: isCreating } =
    useCreateRecognitionAchievement();
  const { mutate: updateAchievement, isLoading: isUpdating } =
    useUpdateRecognitionAchievement();

  const onSubmit: SubmitHandler<Values> = (values) => {
    const valuesToData = {
      name: values.name,
      points: values.points,
      requiresApproval: values.requiresApproval,
      limit: values.limit,
      limitPeriod: values.limitPeriod,
      imageId: values.imageId,
      employeeGroups: values.employeeGroups,
      translations: values.translations.map((trans) => ({
        language: trans.language,
        title: trans.title,
        description: trans.description,
      })),
    } as const;

    if (achievement) {
      updateAchievement(
        { achievementId, data: valuesToData },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
            history.push(ROUTES.recognitions.achievements.list);
          },
        },
      );
    } else {
      createAchievement(valuesToData, {
        onSuccess: () => {
          predefinedToasts.success.added();
          history.push(ROUTES.recognitions.achievements.list);
        },
      });
    }
  };

  const isDirty = !!Object.keys(dirtyFields).length;

  const [imageId] = watch(['imageId']);

  if (achievementId && isLoading) return <Loader />;
  if (achievementId && !achievement) return <NothingFoundAlert />;

  return (
    <Stack>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('recognition.recognitionSettings'),
            url: ROUTES.recognitions.list,
          },
          {
            label: t('recognition.achievementsTab'),
            url: ROUTES.recognitions.achievements.list,
          },
          {
            label: !achievementId
              ? t('common.create_draft')
              : achievement
              ? achievement.name
              : '',
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'settings',
                label: t('common.settings'),
                url: achievementId
                  ? ROUTES.recognitions.achievements.details.replace(
                      ':id',
                      achievementId,
                    )
                  : ROUTES.recognitions.achievements.create,
                isActive:
                  path === ROUTES.recognitions.achievements.details ||
                  path === ROUTES.recognitions.achievements.create,
                disabled: !achievement,
              },
            ]}
          />
        }
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Panel title={t('benefits.benefitGeneralSettingsSection')}>
              <Stack spacing={2}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextField
                      label={t('common.systemName')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="points"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <NumberInput
                      label={t('recognition.achievementPoints', {
                        recognitionCurrency: pointsLabel,
                      })}
                      name={name}
                      value={value}
                      onChange={onChange}
                      key={value}
                      required
                      ref={ref}
                    />
                  )}
                />

                <Stack gap={2} flexDirection="row">
                  <Controller
                    name="limit"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <NumberInput
                          label={t('recognition.achievementAssignmentLimit')}
                          name={name}
                          value={value}
                          onChange={onChange}
                          type="integer"
                          key={value}
                          sx={{ width: '100%' }}
                          infoMessage={t(
                            'recognition.achievementAssignmentLimitHint',
                          )}
                          ref={ref}
                        />
                      );
                    }}
                  />

                  <Controller
                    name="limitPeriod"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <SelectInput
                          name={name}
                          value={value}
                          onChange={onChange}
                          label={t('recognition.allowanceRulesListFrequency')}
                          options={frequencyUnits
                            .filter((period) =>
                              ['weekly', 'monthly', 'yearly'].includes(period),
                            )
                            .map((period) => ({
                              label: t(
                                `recognition.allowanceRuleFrequency-${period}`,
                              ),
                              value: period,
                            }))}
                          ref={ref}
                        />
                      );
                    }}
                  />
                </Stack>

                <Controller
                  name="requiresApproval"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SwitchInput
                      label={t('recognition.achievementRequiresApproval')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="employeeGroups"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <AutocompleteInput
                        label={t('recognition.achievementAccessibleByGroups')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={
                          employeesGroups?.items.map((group) => ({
                            label: group.name,
                            value: group.id,
                          })) ?? []
                        }
                        disabled={isEmployeeGroupsLoading}
                        multiple
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="imageId"
                  control={control}
                  render={({ field: { name, onChange, ref } }) => {
                    return imageId ? (
                      <ImageList
                        items={[
                          {
                            imageId,
                            actions: [
                              {
                                icon: Delete,
                                variant: 'neutral-outline',
                                onClick: () => onChange(''),
                              },
                            ],
                          },
                        ]}
                      />
                    ) : (
                      <FileInput
                        label={t('recognition.achievementImage')}
                        name={name}
                        onChange={([file]) => {
                          if (file) {
                            uploadFile(
                              { file },
                              {
                                onSuccess: ({ data }) => {
                                  onChange(data.id);
                                },
                              },
                            );
                          } else {
                            onChange('');
                          }
                        }}
                        disabled={isUploading}
                        maxFiles={1}
                        previewSelectedFiles={false}
                        accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                        ref={ref}
                      />
                    );
                  }}
                />
              </Stack>
            </Panel>

            <Panel title={t('benefitTemplates.templateContent')}>
              <Stack gap={2}>
                <Controller
                  name="languages"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        label={t('companies.supported_languages')}
                        name={name}
                        value={value}
                        onChange={(value) => {
                          const selectedLanguages = value as Array<
                            Uppercase<ContentLanguage>
                          >;
                          replaceTranslations(
                            selectedLanguages.map(
                              (language) =>
                                getValues('translations').find(
                                  (t) => t.language === language,
                                ) ?? {
                                  language,
                                  title: '',
                                  description: '',
                                },
                            ),
                          );
                          onChange(selectedLanguages);
                        }}
                        options={supportedLanguages.map((language) => ({
                          value: language,
                          label: formatLanguage(i18n.language, language),
                          disabled: language === defaultLanguage,
                        }))}
                        multiple
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <MultiLanguageInputContainer
                  label={t('recognition.achievementTitle')}
                  info={t('recognition.achievementTitleHint')}
                  required
                  fields={translationsFields.map((field, i) => ({
                    id: field.id,
                    language: field.language,
                    component: (
                      <Controller
                        name={`translations.${i}.title`}
                        control={control}
                        render={({ field: { name, value, onChange, ref } }) => {
                          return (
                            <TextField
                              name={name}
                              value={value}
                              onChange={onChange}
                              required
                              ref={ref}
                            />
                          );
                        }}
                      />
                    ),
                  }))}
                />

                <MultiLanguageInputContainer
                  label={t('recognition.achievementDescription')}
                  info={t('recognition.achievementDescriptionHint')}
                  fields={translationsFields.map((field, i) => ({
                    id: field.id,
                    language: field.language,
                    component: (
                      <Controller
                        name={`translations.${i}.description`}
                        control={control}
                        render={({ field: { name, value, onChange, ref } }) => {
                          return (
                            <TextField
                              name={name}
                              value={value}
                              onChange={onChange}
                              rows={3}
                              ref={ref}
                            />
                          );
                        }}
                      />
                    ),
                  }))}
                />
              </Stack>
            </Panel>
          </Stack>

          <Footer visible={isDirty}>
            <Stack direction="row" justifyContent="end" gap="10px">
              <Button
                label={t('common.cancel')}
                variant="neutral-outline"
                onClick={() => {
                  reset(undefined, { keepDirty: false });
                }}
              />
              <Button
                label={t('common.save')}
                type="submit"
                disabled={isCreating || isUpdating}
              />
            </Stack>
          </Footer>
        </form>

        <DataLossPrompt when={isDirty && !isUpdating && !isCreating} />
      </PageContentLayout>
    </Stack>
  );
};
